/** @jsxImportSource @emotion/react */
import React from 'react'
import { Button, Modal } from 'react-bootstrap'
import { skipToken } from '@reduxjs/toolkit/query/react'
import { BillingAttachments } from 'components/lab/billingNotes/BillingAttachments'
import { useGetBillingAttachmentsQuery } from 'store/services/billingAttachments'
import { getShortChargeIdValue } from '../billingCharge/utils'
import { useLabPermission } from 'hooks/useLabPermission'
import { UserPermission } from 'components/lab/user/permissions'

interface Props {
  // The charge whose documents the dialog manages; null keeps it closed.
  chargeId: string | null
  onClose: () => void
  // Fired after an upload or delete so the row indicator's count can refresh.
  onChange?: () => void
}

// Documents dialog for a charge row on the Payments tab: the existing
// ChargeAttachments block (list, upload, delete) lifted into a modal, the way
// notes open from the same table. The list is fetched here, when the dialog
// opens — the row only carries a count.
export const ChargeAttachmentsModal: React.FC<Props> = ({ chargeId, onClose, onChange }) => {
  const canModifyCharge = useLabPermission(UserPermission.MODIFY_BILLING_CHARGES)
  const { data: attachments = [] } = useGetBillingAttachmentsQuery(
    chargeId ? { owner: { type: 'charge', id: chargeId } } : skipToken,
  )

  return (
    <Modal show={Boolean(chargeId)} onHide={onClose} centered>
      <Modal.Header>
        <Modal.Title css={{ fontSize: '1.1rem' }}>
          Attachments — charge {chargeId ? getShortChargeIdValue(chargeId) : ''}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {chargeId && (
          <BillingAttachments
            owner={{ type: 'charge', id: chargeId }}
            attachments={attachments}
            onChange={onChange}
            canModify={canModifyCharge}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" size="sm" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

// Documents on a posted payment. There is one that matters: the paper EOB
// behind an adjudication somebody keyed in by hand, which the next payer in the
// waterfall needs attached to its COB claim — an electronic remittance carries
// its own and needs nothing here.
export const PaymentAttachmentsModal: React.FC<{
  paymentId: string | null
  // Shown in the title so a biller opening it from a transaction row knows
  // which posting they are filing against.
  sourceLabel?: string | null
  onClose: () => void
  onChange?: () => void
}> = ({ paymentId, sourceLabel, onClose, onChange }) => {
  const canPostInsurancePayments = useLabPermission(UserPermission.POST_INSURANCE_PAYMENTS)
  const { data: attachments = [] } = useGetBillingAttachmentsQuery(
    paymentId ? { owner: { type: 'payment', id: paymentId } } : skipToken,
  )

  return (
    <Modal show={Boolean(paymentId)} onHide={onClose} centered>
      <Modal.Header>
        <Modal.Title css={{ fontSize: '1.1rem' }}>
          Payment documents{sourceLabel ? ` — ${sourceLabel}` : ''}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="text-muted small mb-3">
          Attach the paper EOB for this payment. It is sent with the COB claim to the next payer,
          and printed behind the CMS-1500 on a paper claim. PDF, JPEG or PNG.
        </div>
        {paymentId && (
          <BillingAttachments
            owner={{ type: 'payment', id: paymentId }}
            attachments={attachments}
            onChange={onChange}
            canModify={canPostInsurancePayments}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" size="sm" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

// The row affordance: a paperclip pill carrying the count, shaped like the
// notes indicator it sits beside.
export const AttachmentsIndicator: React.FC<{
  count: number
  isOpen: boolean
  onToggle: () => void
}> = ({ count, isOpen, onToggle }) => {
  const hasFiles = count > 0

  return (
    <button
      type="button"
      title={hasFiles ? `${count} attachment${count === 1 ? '' : 's'}` : 'Attach a file'}
      onClick={(event) => {
        event.stopPropagation()
        onToggle()
      }}
      css={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.3rem',
        borderRadius: '999px',
        padding: '0.1rem 0.55rem',
        fontSize: '0.75rem',
        fontWeight: 600,
        lineHeight: 1.5,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        appearance: 'none',
        transition: 'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease',
        border: `1px solid ${hasFiles ? '#007bff' : '#ced4da'}`,
        backgroundColor: hasFiles ? '#007bff' : 'transparent',
        color: hasFiles ? '#ffffff' : '#6c757d',
        boxShadow: isOpen ? '0 0 0 2px rgba(0, 123, 255, 0.35)' : 'none',
        '&:hover': {
          backgroundColor: hasFiles ? '#0069d9' : '#f1f3f5',
        },
      }}>
      <i className="fas fa-paperclip" />
      {hasFiles ? count : 'File'}
    </button>
  )
}
